// src/pages/Notifications/NotificationsPage.js
// Экран уведомлений: новые сверху, непрочитанные выделены, «Прочитать все», детали по нажатию
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, ButtonBase, CircularProgress, Link, Typography } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/NotificationsNone';
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../../services/notificationsService';
import { refreshUnreadCount, setUnreadCount } from '../../hooks/useUnreadCount';
import { extractErrorMessage } from '../../utils/errors';
import { CARD_SURFACE_SX, MUTED_TEXT_COLOR } from '../../styles/tokens';
import NotificationDetailsDialog from '../../components/notifications/NotificationDetailsDialog';
import { formatNotificationTime, notificationLook } from '../../components/notifications/notificationMeta';

const ACCENT = '#ff7043';
const PAGE_SIZE = 30;

const textButtonSx = {
  height: 36,
  px: 1.5,
  borderRadius: '10px',
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: 600,
  color: '#e4e4e7',
  backgroundColor: 'rgba(255,255,255,0.06)',
  boxShadow: 'none',
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', boxShadow: 'none' },
};

function NotificationRow({ item, onOpen }) {
  const unread = !item.read_at;
  const { color, Icon } = notificationLook(item);

  return (
    <ButtonBase
      onClick={() => onOpen(item)}
      aria-label={`${unread ? 'Непрочитанное. ' : ''}${item.title}. ${item.body}`}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: '12px',
        textAlign: 'left',
        borderRadius: '12px',
        backgroundColor: unread ? 'rgba(255,255,255,0.05)' : 'transparent',
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.07)' },
        '&.Mui-focusVisible': { outline: `2px solid ${ACCENT}`, outlineOffset: '-2px' },
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: `${color}26`,
        }}
      >
        <Icon sx={{ fontSize: 20, color }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: '15px',
              fontWeight: unread ? 700 : 500,
              color: unread ? '#fff' : '#d4d4d8',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.title}
          </Typography>
          <Typography sx={{ flexShrink: 0, fontSize: '12px', color: MUTED_TEXT_COLOR, whiteSpace: 'nowrap' }}>
            {formatNotificationTime(item.created_at)}
          </Typography>
        </Box>
        <Typography sx={{ mt: 0.25, fontSize: '14px', color: unread ? '#e4e4e7' : MUTED_TEXT_COLOR, wordBreak: 'break-word' }}>
          {item.body}
        </Typography>
      </Box>
      <Box
        aria-hidden="true"
        sx={{
          flexShrink: 0,
          width: 8,
          height: 8,
          mt: '7px',
          borderRadius: '50%',
          backgroundColor: unread ? ACCENT : 'transparent',
        }}
      />
    </ButtonBase>
  );
}

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Переход из push: /notifications?id=<id> — открыть детали этого уведомления
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedId = Number(searchParams.get('id')) || null;

  const loadPage = useCallback(async (before) => {
    const page = await fetchNotifications({ limit: PAGE_SIZE, before });
    setItems((prev) => (before ? [...prev, ...page.items] : page.items));
    setNextBefore(page.next_before);
  }, []);

  useEffect(() => {
    let alive = true;
    loadPage()
      .then(() => refreshUnreadCount())
      .catch((err) => alive && setError(extractErrorMessage(err)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [loadPage]);

  const handleMore = async () => {
    if (loadingMore || !nextBefore) return;
    setLoadingMore(true);
    try {
      await loadPage(nextBefore);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoadingMore(false);
    }
  };

  const handleOpen = async (item) => {
    setSelected(item);
    setDetailsOpen(true);
    if (item.read_at) return;
    // Сразу отмечаем в списке; ошибка запроса не мешает смотреть детали
    const readAt = new Date().toISOString();
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, read_at: readAt } : i)));
    try {
      await markNotificationRead(item.id);
    } catch {
      // не отметилось — счётчик ниже покажет реальное состояние
    } finally {
      refreshUnreadCount();
    }
  };

  const handleReadAll = async () => {
    try {
      await markAllNotificationsRead();
      const readAt = new Date().toISOString();
      setItems((prev) => prev.map((i) => (i.read_at ? i : { ...i, read_at: readAt })));
      setUnreadCount(0);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  useEffect(() => {
    if (loading || !linkedId) return;
    setSearchParams({}, { replace: true }); // повторное открытие страницы не откроет детали снова
    const openLinked = async () => {
      let linked = items.find((i) => i.id === linkedId);
      if (!linked) {
        // Страница уже была открыта, а push — о новом уведомлении: перечитываем первую страницу
        const page = await fetchNotifications({ limit: PAGE_SIZE });
        setItems(page.items);
        setNextBefore(page.next_before);
        linked = page.items.find((i) => i.id === linkedId);
      }
      if (linked) handleOpen(linked);
    };
    openLinked().catch((err) => setError(extractErrorMessage(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, linkedId]);

  const hasUnread = items.some((i) => !i.read_at);

  return (
    <Box sx={{ width: '100%', maxWidth: 720, mx: 'auto', pt: { xs: 1, md: 3 }, pb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Typography component="h1" sx={{ fontSize: { xs: '22px', md: '26px' }, fontWeight: 800, color: '#fff' }}>
          Уведомления
        </Typography>
        {hasUnread && (
          <Button onClick={handleReadAll} sx={textButtonSx}>
            Прочитать все
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, whiteSpace: 'pre-line' }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ ...CARD_SURFACE_SX, p: 4, textAlign: 'center' }}>
          <NotificationsIcon sx={{ fontSize: 40, color: MUTED_TEXT_COLOR }} />
          <Typography sx={{ mt: 1, fontSize: '16px', fontWeight: 700, color: '#fff' }}>Уведомлений пока нет</Typography>
          <Typography sx={{ mt: 0.5, fontSize: '14px', color: MUTED_TEXT_COLOR }}>
            Сообщим об удалениях заказов и других событиях в ресторане.{' '}
            <Link component={RouterLink} to="/profile" sx={{ color: '#ff8a65' }}>
              Настроить
            </Link>
          </Typography>
        </Box>
      ) : (
        <Box sx={{ ...CARD_SURFACE_SX, p: '6px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {items.map((item) => (
            <NotificationRow key={item.id} item={item} onOpen={handleOpen} />
          ))}
        </Box>
      )}

      {nextBefore && !loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button onClick={handleMore} disabled={loadingMore} sx={textButtonSx}>
            {loadingMore ? <CircularProgress size={18} /> : 'Показать ещё'}
          </Button>
        </Box>
      )}

      <NotificationDetailsDialog item={selected} open={detailsOpen} onClose={() => setDetailsOpen(false)} />
    </Box>
  );
}
